import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { RefreshActions } from '../state/refresh'
import { useRequests } from '../requests/useRequests'

interface BrandFormData {
  name: string
  image?: string
  description?: string
  website?: string
  contact?: string
  facebook?: string
  instagram?: string
  youtube?: string
  priority?: number
}

export const useBrandForm = (isEditMode: boolean) => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requests = useRequests()

  const [formData, setFormData] = useState<BrandFormData>({
    name: '',
    image: '',
    description: '',
    website: '',
    contact: '',
    facebook: '',
    instagram: '',
    youtube: '',
    priority: 0
  })

  const [loading, setLoading] = useState<boolean>(false)

  const showToast = (message: string) => {
    dispatch(
      RefreshActions.setToastMessage({
        message
      })
    )
  }

  useEffect(() => {
    if (isEditMode && id) {
      setLoading(true)

      const brandId = parseInt(id)
      if (isNaN(brandId)) {
        showToast('Nieprawidłowy identyfikator marki')
        navigate('/brands')
        return
      }

      requests.getBrand(brandId)
        .then(response => {
          const brandData = response.data
          setFormData({
            name: brandData.name,
            image: brandData.image || '',
            description: brandData.description || '',
            website: brandData.website || '',
            contact: brandData.contact || '',
            facebook: brandData.facebook || '',
            instagram: brandData.instagram || '',
            youtube: brandData.youtube || '',
            priority: Number(brandData.priority) || 0
          })
          setLoading(false)
        })
        .catch(error => {
          console.error('Błąd podczas pobierania danych marki:', error)
          showToast('Nie udało się pobrać danych marki')
          setLoading(false)
          navigate('/brands')
        })
    }
  }, [id, isEditMode, navigate, dispatch])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name) {
      showToast('Wypełnij wszystkie wymagane pola')
      return
    }

    setLoading(true)

    try {
      if (isEditMode) {
        await requests.updateBrand(parseInt(id!), { ...formData, priority: Number(formData.priority) || 0 })
        showToast('Marka została zaktualizowana')
      } else {
        await requests.createBrand({ ...formData, priority: Number(formData.priority) || 0 })
        showToast('Nowa marka została dodana')
      }

      navigate('/brands')
    } catch (error) {
      console.error('Błąd podczas zapisywania marki:', error)
      showToast('Wystąpił błąd podczas zapisywania marki')
    } finally {
      setLoading(false)
    }
  }

  return {
    formData,
    loading,
    handleChange,
    handleSubmit,
    navigate
  }
}
